import React, { useState, useMemo, useEffect } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  FlatList,
  TextInput,
  Image,
  Platform,
  Alert,
  Modal,
  ActivityIndicator
} from "react-native";
import { AntDesign, MaterialIcons, Ionicons } from "@expo/vector-icons";
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import { useDeviceType } from "@hooks/useDeviceType";
import { getModernCardStyle, getModernTextColor, getModernActionStyle } from "@stylesheet";
import { XStack, YStack, Separator, SizableText, H4, Avatar } from "tamagui";
import * as Animatable from 'react-native-animatable';
import apis from '@api/api';

interface User {
  id: string;
  name: string;
  role: string;
  email?: string;
  status: 'active' | 'inactive' | 'pending';
  imgSrc?: string;
  joinDate?: string;
  lastActive?: string;
  department?: string;
  _id?: string;
  isPending?: boolean;
}

const UserTable = () => {
  const theme = themeSelector();
  // State management
  const [searchText, setSearchText] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [showPerPage, setShowPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedUserForRole, setSelectedUserForRole] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const roles = ['admin', 'user', 'manager', 'developer', 'support', 'owner'];
  const statusOptions = ['All', 'active', 'inactive', 'pending'];

  // Helper to display status with proper capitalization
  const getDisplayStatus = (status: string) => {
    return status === 'All' ? status : status.charAt(0).toUpperCase() + status.slice(1);
  };

  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const teamResponse = await apis.getAllMessagingUsers(1, 100, false);
      const allUsers = teamResponse?.data || [];

      const validUsers = allUsers.filter((user: any) => {
        if (user.isGroup) return false;
        return user && (user._id || user.id);
      });

      const normalizeStatus = (user: any): string => {
        if (user.isPending) return 'pending';
        return (user.companyUserStatus || user.status || 'active').toLowerCase();
      };

      const transformedUsers: User[] = validUsers.map((user: any) => ({
        id: user._id || user.id,
        _id: user._id || user.id,
        name: user.profile_information?.name
          ? `${user.profile_information.name} ${user.profile_information.surname || ''}`.trim()
          : user.email || 'Unknown User',
        email: user.email,
        role: user.role || 'user',
        status: normalizeStatus(user),
        imgSrc: user.profile_information?.avatar?.img,
        department: user.department,
        isPending: !!user.isPending,
        joinDate: user.createdAt
          ? new Date(user.createdAt).toLocaleDateString()
          : undefined,
        lastActive: normalizeStatus(user) === 'active' ? 'Recently' : 'Some time ago',
      }));

      setUsers(transformedUsers);

      if (transformedUsers.length === 0) {
        setError(
          'No users found. You may need to add users to your company or connect with other users.'
        );
      }
    } catch (err: any) {
      console.error('❌ Error fetching users:', err);
      setError(err.message || 'Failed to fetch users');
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const statusOrder: Record<string, number> = {
    active: 1,
    pending: 2,
    inactive: 3,
  };

  const filteredUsers = useMemo(() => {
    const filtered = users.filter(user => {
      const matchesSearch =
        user.name.toLowerCase().includes(searchText.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchText.toLowerCase()) ||
        user.role.toLowerCase().includes(searchText.toLowerCase());

      const matchesStatus =
        selectedStatus === 'All' || user.status === selectedStatus;

      return matchesSearch && matchesStatus;
    });

    return filtered.sort((a, b) => {
      // 1️⃣ Owner first
      if (a.role === 'owner' && b.role !== 'owner') return -1;
      if (a.role !== 'owner' && b.role === 'owner') return 1;

      // 2️⃣ Status order
      const orderA = statusOrder[a.status] ?? 99;
      const orderB = statusOrder[b.status] ?? 99;
      if (orderA !== orderB) return orderA - orderB;

      // 3️⃣ Name
      return a.name.localeCompare(b.name);
    });
  }, [users, searchText, selectedStatus]);

  const totalPages = Math.ceil(filteredUsers.length / showPerPage);
  const startIndex = (currentPage - 1) * showPerPage;
  const endIndex = startIndex + showPerPage;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);

  // Helper functions
  const toggleUserSelection = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === currentUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(currentUsers.map(user => user.id));
    }
  };

  const handleRoleAssignment = (user: User) => {
    setSelectedUserForRole(user);
    setShowRoleModal(true);
  };

  const assignRole = async (newRole: string) => {
    if (selectedUserForRole) {
      Alert.alert(
        'Role Updated',
        `${selectedUserForRole.name}'s role has been updated to ${newRole}`,
        [{ text: 'OK' }]
      );
      setShowRoleModal(false);
      setSelectedUserForRole(null);
      // Refetch users to get updated data
      await fetchUsers();
    }
  };

  const getStatusColor = (status: string) => {
    const lowerStatus = status.toLowerCase();
    if (lowerStatus === 'active') {
      return { backgroundColor: '#10b981', color: 'white' };
    } else if (lowerStatus === 'pending') {
      return { backgroundColor: '#f59e0b', color: 'white' };
    } else {
      return { backgroundColor: '#ef4444', color: 'white' };
    }
  };

  // Header component
  const renderHeader = () => (
    <View style={[
      getModernCardStyle(theme, 'medium'),
      { marginBottom: 16, padding: 20, width: '100%' }
    ]}>
      <XStack alignItems="center" justifyContent="space-between" marginBottom={16}>
        <H4 style={{
          color: getModernTextColor(theme, 'primary'),
          fontWeight: '700'
        }}>
          User Management
        </H4>

        <TouchableOpacity
          onPress={fetchUsers}
          style={[
            getModernActionStyle(theme, 'blue'),
            { paddingHorizontal: 12, minWidth: 120, paddingVertical: 8, maxWidth: 120, borderRadius: 8 }
          ]}
          disabled={isLoading}
        >
          <XStack alignItems="center" gap={6}>
            <MaterialIcons
              name="refresh"
              size={16}
              color="white"
            />
            <SizableText size="$2" color="white" fontWeight="600">
              {isLoading ? 'Loading...' : 'Refresh'}
            </SizableText>
          </XStack>
        </TouchableOpacity>
      </XStack>

      {error && (
        <View style={{
          backgroundColor: theme ? '#fee2e2' : '#7f1d1d',
          padding: 12,
          borderRadius: 8,
          marginBottom: 12
        }}>
          <SizableText size="$3" color={theme ? '#991b1b' : '#fecaca'}>
            ⚠️ {error}
          </SizableText>
        </View>
      )}

      {/* Controls Row */}
      <XStack gap={12} flexWrap="wrap" alignItems="center" justifyContent="space-between" width="100%">
        <XStack gap={8} alignItems="center" flex={1} minWidth={120}>
          <SizableText size="$3" color={getModernTextColor(theme, 'secondary')}>Show</SizableText>
          <TouchableOpacity
            style={[
              getModernActionStyle(theme, 'grey'),
              { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8, minWidth: 60 }
            ]}
            onPress={() => {
              // Show picker for entries per page
              Alert.alert(
                'Entries per page',
                'Choose how many entries to show',
                [
                  { text: '5', onPress: () => setShowPerPage(5) },
                  { text: '10', onPress: () => setShowPerPage(10) },
                  { text: '20', onPress: () => setShowPerPage(20) },
                  { text: 'Cancel', style: 'cancel' }
                ]
              );
            }}
          >
            <SizableText size="$3" color={getModernTextColor(theme, 'primary')}>
              {showPerPage}
            </SizableText>
          </TouchableOpacity>
        </XStack>

        {/* Search Input */}
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          backgroundColor: theme ? '#e2e8f0' : '#374151',
          borderRadius: 12,
          paddingHorizontal: 12,
          paddingVertical: 8,
          flex: 2,
          minWidth: 150,
          maxWidth: 250,
        }}>
          <AntDesign
            name="search"
            size={16}
            color={getModernTextColor(theme, 'muted')}
            style={{ marginRight: 8 }}
          />
          <TextInput
            style={{
              flex: 1,
              color: getModernTextColor(theme, 'primary'),
              fontSize: 14
            }}
            placeholder="Search users..."
            placeholderTextColor={getModernTextColor(theme, 'muted')}
            value={searchText}
            onChangeText={setSearchText}
          />
        </View>

        {/* Status Filter */}
        <TouchableOpacity
          style={[
            getModernActionStyle(theme, 'blue'),
            { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8, minWidth: 80 }
          ]}
          onPress={() => {
            Alert.alert(
              'Filter by Status',
              'Choose status to filter',
              [
                ...statusOptions.map(status => ({
                  text: getDisplayStatus(status),
                  onPress: () => setSelectedStatus(status)
                })),
                { text: 'Cancel', style: 'cancel' }
              ]
            );
          }}
        >
          <SizableText size="$3" color="white">
            {getDisplayStatus(selectedStatus)}
          </SizableText>
        </TouchableOpacity>
      </XStack>
    </View>
  );

  // User item component
  const renderUserItem = ({ item: user, index }: { item: User; index: number }) => {
    const getBorderColor = (status: string) => {
      const lowerStatus = status.toLowerCase();
      if (lowerStatus === 'active') return '#10b981';
      if (lowerStatus === 'pending') return '#f59e0b';
      return '#ef4444';
    };

    return (
      <Animatable.View
        animation="fadeInUp"
        delay={index * 50}
        style={[
          getModernCardStyle(theme, 'medium'),
          {
            marginBottom: 8,
            padding: 16,
            borderLeftWidth: 4,
            borderLeftColor: getBorderColor(user.status),
            width: '100%',
            alignSelf: 'stretch'
          }
        ]}
      >
      <XStack alignItems="center" justifyContent="space-between" width="100%">
        {/* Checkbox and Index */}
        <XStack alignItems="center" gap={12} flex={1} minWidth={60}>
          <TouchableOpacity
            onPress={() => toggleUserSelection(user.id)}
            style={{
              width: 20,
              height: 20,
              borderRadius: 4,
              borderWidth: 2,
              borderColor: selectedUsers.includes(user.id) ? '#2563eb' : '#6b7280',
              backgroundColor: selectedUsers.includes(user.id) ? '#2563eb' : 'transparent',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {selectedUsers.includes(user.id) && (
              <MaterialIcons name="check" size={12} color="white" />
            )}
          </TouchableOpacity>

          <SizableText size="$2" color={getModernTextColor(theme, 'muted')}>
            {String(startIndex + index + 1).padStart(2, '0')}
          </SizableText>
        </XStack>

        {/* User Info */}
        <XStack alignItems="center" gap={12} flex={4} minWidth={200}>
            <Avatar circular size="$4" backgroundColor="#1d4ed8">
              <Avatar.Image
                src={user.imgSrc || "https://storage.googleapis.com/smartsaas-avatar-store/Asset%202.png"}
                alt={`${user.name} avatar`}
              />
            <Avatar.Fallback backgroundColor="#1d4ed8">
                <SizableText color="white" fontWeight="600" fontSize={14}>
                  {user.name.split(' ').filter(n => n).map(n => n[0].toUpperCase()).slice(0, 2).join('')}
              </SizableText>
            </Avatar.Fallback>
          </Avatar>

          <YStack flex={1} minWidth={120}>
            <SizableText
              size="$4"
              fontWeight="600"
              color={getModernTextColor(theme, 'primary')}
              numberOfLines={1}
            >
              {user.name}
            </SizableText>
            <SizableText
              size="$2"
              color={getModernTextColor(theme, 'muted')}
              numberOfLines={1}
            >
              {user.email}
            </SizableText>
            <SizableText
              size="$1"
              color={getModernTextColor(theme, 'muted')}
            >
              Last active: {user.lastActive}
            </SizableText>
          </YStack>
        </XStack>

        {/* Role and Status */}
          <XStack alignItems="center" gap={8} flex={2} minWidth={100}>
          {/* <View style={[
            getModernActionStyle(theme, 'blue'),
            { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6, backgroundColor: '#7c3aed', minWidth: 80 }
          ]}>
            <SizableText size="$2" color="white" fontWeight="600" textAlign="center">
              {user.role}
            </SizableText>
          </View> */}

          <View style={[
            getStatusColor(user.status),
            { paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4, minWidth: 60 }
          ]}>
            <SizableText size="$1" color="white" fontWeight="500" textAlign="center">
                {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
            </SizableText>
          </View>
          </XStack>

        {/* Action Button */}
          <View style={{ flex: 1, minWidth: 140, alignItems: 'flex-end' }}>
          <TouchableOpacity
            style={[
              getModernActionStyle(theme, 'blue'),
                { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8, minWidth: 140 }
            ]}
            onPress={() => handleRoleAssignment(user)}
          >
              <SizableText numberOfLines={1} size="$2" color="white" fontWeight="600" textAlign="center">
              Assign Role
            </SizableText>
          </TouchableOpacity>
        </View>
      </XStack>
    </Animatable.View>
    );
  };

  // Pagination component
  const renderPagination = () => (
    <View style={[
      getModernCardStyle(theme, 'medium'),
      { marginTop: 16, padding: 16, width: '100%' }
    ]}>
      <XStack alignItems="center" justifyContent="space-between" width="100%">
        <SizableText size="$3" color={getModernTextColor(theme, 'secondary')} flex={1}>
          Showing {startIndex + 1} to {Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} entries
        </SizableText>

        <XStack alignItems="center" gap={8}>
          <TouchableOpacity
            disabled={currentPage === 1}
            onPress={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            style={[
              getModernActionStyle(theme, currentPage === 1 ? 'grey' : 'blue'),
              { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 6 }
            ]}
          >
            <MaterialIcons
              name="chevron-left"
              size={16}
              color={currentPage === 1 ? getModernTextColor(theme, 'muted') : 'white'}
            />
          </TouchableOpacity>

          <SizableText size="$3" color={getModernTextColor(theme, 'primary')} fontWeight="600">
            {currentPage} / {totalPages}
          </SizableText>

          <TouchableOpacity
            disabled={currentPage === totalPages}
            onPress={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            style={[
              getModernActionStyle(theme, currentPage === totalPages ? 'grey' : 'blue'),
              { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 6 }
            ]}
          >
            <MaterialIcons
              name="chevron-right"
              size={16}
              color={currentPage === totalPages ? getModernTextColor(theme, 'muted') : 'white'}
            />
          </TouchableOpacity>
        </XStack>
      </XStack>
    </View>
  );

  // Role assignment modal
  const renderRoleModal = () => (
    <Modal
      visible={showRoleModal}
      transparent
      animationType="fade"
      onRequestClose={() => setShowRoleModal(false)}
    >
      <View style={{
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20
      }}>
        <View style={[
          getModernCardStyle(theme, 'large'),
          { width: '100%', maxWidth: 400, padding: 24 }
        ]}>
          <H4 style={{
            color: getModernTextColor(theme, 'primary'),
            marginBottom: 16,
            textAlign: 'center'
          }}>
            Assign Role to {selectedUserForRole?.name}
          </H4>

          <YStack gap={8}>
            {roles.map(role => (
              <TouchableOpacity
                key={role}
                style={[
                  getModernActionStyle(theme, 'grey'),
                  {
                    padding: 12,
                    borderRadius: 8,
                    minWidth: 120,
                    backgroundColor: role === selectedUserForRole?.role
                      ? (theme ? '#2563eb' : '#1e40af')
                      : (theme ? '#e2e8f0' : '#374151')
                  }
                ]}
                onPress={() => assignRole(role)}
              >
                <SizableText
                  size="$3"
                  color={role === selectedUserForRole?.role
                    ? 'white'
                    : getModernTextColor(theme, 'primary')
                  }
                  fontWeight={role === selectedUserForRole?.role ? '600' : '400'}
                  textAlign="center"
                >
                  {role.charAt(0).toUpperCase() + role.slice(1)}
                </SizableText>
              </TouchableOpacity>
            ))}
          </YStack>

          <TouchableOpacity
            style={[
              getModernActionStyle(theme, 'red'),
              { padding: 12, borderRadius: 8, marginTop: 16, minWidth: 120 }
            ]}
            onPress={() => setShowRoleModal(false)}
          >
            <SizableText size="$3" color="white" fontWeight="600" textAlign="center">
              Cancel
            </SizableText>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  // Show loading spinner on initial load
  if (isLoading && users.length === 0) {
    return (
      <View style={{ flex: 1, width: '100%', justifyContent: 'center', alignItems: 'center', padding: 40 }}>
        <ActivityIndicator size="large" color={theme ? '#2563eb' : '#60a5fa'} />
        <SizableText
          size="$4"
          color={getModernTextColor(theme, 'secondary')}
          marginTop={16}
          textAlign="center"
        >
          Loading users...
        </SizableText>
      </View>
    );
  }

  return (
    <View style={{ flex: 1, width: '100%' }}>
      <View style={{ paddingHorizontal: Platform.OS === 'web' ? 20 : 16 }}>
        {renderHeader()}

        {/* Select All Header */}
        {currentUsers.length > 0 && (
          <TouchableOpacity
            onPress={toggleSelectAll}
            style={[
              getModernActionStyle(theme, 'blue'),
              {
                padding: 12,
                borderRadius: 8,
                marginBottom: 8,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%'
              }
            ]}
          >
            <SizableText size="$3" color="white" fontWeight="600">
              {selectedUsers.length === currentUsers.length ? 'Deselect All' : 'Select All'}
            </SizableText>
            <SizableText size="$2" color="white">
              ({selectedUsers.length}/{currentUsers.length})
            </SizableText>
          </TouchableOpacity>
        )}
      </View>

      <FlatList
        data={currentUsers}
        renderItem={renderUserItem}
        keyExtractor={(item) => item.id.toString()}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{
          paddingHorizontal: Platform.OS === 'web' ? 20 : 16,
          paddingBottom: 20
        }}
        style={{ width: '100%' }}
        ListEmptyComponent={() => (
          <View style={[
            getModernCardStyle(theme, 'medium'),
            { padding: 40, alignItems: 'center', width: '100%' }
          ]}>
            <MaterialIcons
              name="people-outline"
              size={48}
              color={getModernTextColor(theme, 'muted')}
            />
            <SizableText
              size="$4"
              color={getModernTextColor(theme, 'muted')}
              textAlign="center"
              marginTop={16}
            >
              No users found
            </SizableText>
            <SizableText
              size="$3"
              color={getModernTextColor(theme, 'muted')}
              textAlign="center"
              marginTop={8}
            >
              Try adjusting your search or filters
            </SizableText>
          </View>
        )}
      />

      {filteredUsers.length > 0 && (
        <View style={{ paddingHorizontal: Platform.OS === 'web' ? 20 : 16 }}>
          {renderPagination()}
        </View>
      )}
      {renderRoleModal()}
    </View>
  );
};

export default UserTable;
